import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ProductOption } from 'main/consts';

import { ReactComponent as UpArrow } from '../assets/icons/up-arrow-svgrepo-com.svg';

const CloseChipSVG = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={16}
      width={16}
      viewBox="0 0 24 24"
      preserveAspectRatio="none"
      style={{ marginTop: '4px' }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
        fill="#ee2737"
      />
    </svg>
  );
};

interface MultipleSelectProps {
  selectedOptions: ProductOption[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<ProductOption[]>>;
  PRODUCT_OPTIONS: ProductOption[];
}

export const MultipleSelectWithChips: React.FC<MultipleSelectProps> = ({
  selectedOptions,
  setSelectedOptions,
  PRODUCT_OPTIONS,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const shouldFloat = useMemo(() => isOpen || selectedOptions.length > 0, [
    isOpen,
    selectedOptions.length,
  ]);

  const toggleOption = (option: ProductOption) => {
    setSelectedOptions(prev =>
      prev.some(item => item.id === option.id)
        ? prev.filter(item => item.id !== option.id)
        : [...prev, option],
    );
  };

  const removeChip = (optionId: string) => {
    setSelectedOptions(prev => prev.filter(item => item.id !== optionId));
  };

  const handleFocus = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent, option?: ProductOption) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.focus();
    } else if (option && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      toggleOption(option);
    } else if (e.key === 'Tab' && isOpen) {
      if (
        !e.shiftKey &&
        e.target ===
          wrapperRef.current?.querySelector('.select-option:last-child')
      ) {
        e.preventDefault();
        (wrapperRef.current?.querySelector(
          '.select-option',
        ) as HTMLElement)?.focus();
      } else if (
        e.shiftKey &&
        e.target === wrapperRef.current?.querySelector('.select-option')
      ) {
        e.preventDefault();
        (wrapperRef.current?.querySelector(
          '.select-option:last-child',
        ) as HTMLElement)?.focus();
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', marginBottom: '12px' }}
    >
      <label
        id="products-label"
        style={{
          position: 'absolute',
          left: '12px',
          top: shouldFloat ? '12px' : '20px',
          pointerEvents: 'none',
          transition: 'all 0.2s ease-out',
          transformOrigin: 'left center',
          zIndex: shouldFloat ? 1 : 0,
        }}
        onClick={() => wrapperRef.current?.focus()}
        className={`label contact-form-label ${
          shouldFloat
            ? 'multiple-select-input-option-float'
            : 'multiple-select-input-label'
        }`}
      >
        Products of interest
      </label>
      <div
        ref={inputRef}
        className={`asda-input`}
        style={{
          cursor: 'pointer',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '8px',
          minHeight: '70px',
          alignItems: 'center',
          paddingRight: '40px',
          paddingTop: '16px',
          paddingBottom: '16px',
          overflowY: 'auto',
          height: 'fit-content',
          maxHeight: '150px',
          scrollbarWidth: 'thin',
        }}
        tabIndex={0}
        onClick={handleFocus}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="products-listbox"
        aria-labelledby="products-label"
      >
        {(selectedOptions.length !== 0 || isOpen) &&
          selectedOptions.map(option => (
            <div
              key={option.id}
              style={{
                background: 'rgb(60 60 60)',
                borderRadius: '16px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '32px',
              }}
            >
              <span className="multiple-select-chip-text">{option.label}</span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  removeChip(option.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '12px',
                  color: 'white',
                }}
                aria-label={`Remove ${option.label}`}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    removeChip(option.id);
                  }
                }}
              >
                <CloseChipSVG />
              </button>
            </div>
          ))}
        <UpArrow
          className={`multiple-select-input-arrow ${
            isOpen ? 'multiple-select-input-arrow-open' : ''
          }`}
        />
      </div>

      {isOpen && (
        <div
          id="products-listbox"
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'black',
            border: '1px solid #ddd',
            borderRadius: '4px',
            zIndex: 10,
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {PRODUCT_OPTIONS.map(option => (
            <div
              key={option.id}
              className="select-option multiple-select-input-option"
              tabIndex={0}
              role="option"
              aria-selected={selectedOptions.some(
                item => item.id === option.id,
              )}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: selectedOptions.some(item => item.id === option.id)
                  ? 'rgb(60 60 60)'
                  : 'black',
                outline: 'none',
              }}
              onClick={() => toggleOption(option)}
              onKeyDown={e => handleKeyDown(e, option)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
